/**
 * PWA install UX: a header **Install phone** button on first login (captured
 * beforeinstallprompt), a nag after the third tab session, run-on-login hint via chrome://apps
 * or the policy pack.
 */
import { signal } from '@preact/signals';

const SESSIONS_KEY = 'ccphone.tab_sessions';
export const NAG_AFTER_SESSIONS = 3;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export function bumpTabSessions(storage: StorageLike | null): number {
  try {
    const n = Number(storage?.getItem(SESSIONS_KEY) ?? '0') + 1;
    storage?.setItem(SESSIONS_KEY, String(n));
    return n;
  } catch {
    return 1;
  }
}

export function isStandalone(win: Window = window): boolean {
  try {
    return win.matchMedia('(display-mode: standalone)').matches || win.matchMedia('(display-mode: window-controls-overlay)').matches;
  } catch {
    return false;
  }
}

export class InstallPrompt {
  readonly available = signal(false);
  readonly installed = signal(false);
  readonly nag = signal(false);
  private deferred: BeforeInstallPromptEvent | null = null;

  start(win: Window = window, storage: StorageLike | null = null): void {
    this.installed.value = isStandalone(win);
    const sessions = bumpTabSessions(storage);
    this.nag.value = !this.installed.value && sessions >= NAG_AFTER_SESSIONS;
    win.addEventListener('beforeinstallprompt', (ev) => {
      ev.preventDefault();
      this.deferred = ev;
      this.available.value = true;
    });
    win.addEventListener('appinstalled', () => {
      this.installed.value = true;
      this.available.value = false;
      this.nag.value = false;
    });
  }

  async prompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const ev = this.deferred;
    if (!ev) return 'unavailable';
    this.deferred = null;
    this.available.value = false;
    await ev.prompt();
    const choice = await ev.userChoice;
    return choice.outcome;
  }
}
